import { useTranslation } from 'react-i18next';
import { byId, doneBlocks, earnedXP, pad, progress as chapterProgress, totalBlocks, totalXP } from '../engine/progress';
import { useApp, useDue } from '../state/AppProvider';
import { setLang, useLang, SUPPORTED_LANGS } from '../i18n';
import { EnterByMenu } from './EnterByMenu';
import type { Lang } from '../types';

const LANG_SHORT: Record<Lang, string> = { en: 'EN', fr: 'FR' };

export function TopBar() {
  const { t } = useTranslation();
  const { progress, sel, selectNode, startReview, notify, about } = useApp();
  const due = useDue();
  const lang = useLang();

  const node = sel ? byId(sel) : null;
  const xp = earnedXP(progress);
  const total = totalXP();
  const pct = total ? Math.round((xp / total) * 100) : 0;

  return (
    <header className="topnav" data-od-id="topnav">
      <div className="topnav-inner">
        <button type="button" className="brand" data-act="overview" data-od-id="brand" onClick={() => selectNode(null)}>
          <span className="logo" id="brand-name">
            {t('brand')}
          </span>
          <span className="sub" id="brand-sub">
            {t('brandSub')}
          </span>
        </button>

        <div className="crumbs" id="crumbs" data-od-id="breadcrumb" hidden={!node}>
          {node && (
            <>
              <span className="cn num">{pad(node)}</span>
              <span className="ct">{t(node.titleKey)}</span>
              <span className="meta num">
                {chapterProgress(node, progress)}/{node.blocks.length}
              </span>
            </>
          )}
        </div>

        <div className="nav-right">
          <EnterByMenu />

          {/* The artifact set `hidden` on this chip when nothing was due, and the
              chip's own `display: inline-flex` defeated it — so it ships as an
              empty pill with its dot. The pixels are kept; only the accessible
              name is repaired, because an unnamed button is not a state. */}
          <button
            type="button"
            className="chip chip-review"
            id="review-chip"
            data-act="review"
            data-od-id="review-chip"
            hidden={!due.length}
            aria-label={due.length ? `${t('review')} — ${due.length} ${t('dueN')}` : t('reviewNone')}
            title={t('reviewHint')}
            onClick={() => (due.length ? startReview(due[0].id) : notify(t('reviewNone')))}
          >
            <span className="dot" aria-hidden="true" />
            <span id="review-word">{due.length > 0 ? `${t('review')} ·` : null}</span>
            <span className="n num" id="review-n">
              {due.length > 0 ? due.length : null}
            </span>
          </button>

          <div className="meter" data-od-id="xp-meter">
            <span className="xp num">
              <span id="meter-xp">{xp}</span>
              <em id="meter-total">
                {' / '}
                {total} {t('xp')}
              </em>
            </span>
            <span
              className="bar"
              role="progressbar"
              id="meter-bar-wrap"
              aria-label={t('statProgress')}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pct}
              aria-valuetext={`${doneBlocks(progress)}/${totalBlocks()} · ${xp}/${total} ${t('xp')}`}
            >
              <i id="meter-bar" style={{ width: `${pct}%` }} />
            </span>
          </div>

          <div className="seg" role="group" aria-label={t('langLabel')} data-od-id="lang-toggle">
            {SUPPORTED_LANGS.map((code) => (
              <button
                key={code}
                type="button"
                data-act="lang"
                data-lang={code}
                aria-pressed={lang === code}
                onClick={() => setLang(code)}
              >
                {LANG_SHORT[code]}
              </button>
            ))}
          </div>

          <button type="button" className="btn btn-secondary btn-sm" data-act="about" id="about-button" onClick={about.show}>
            <span id="about-btn">{t('about')}</span>
          </button>
        </div>
      </div>
    </header>
  );
}
